import { message } from 'antd';
import { memo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Icon } from 'semantic-ui-react';

import { startCreateAuction, uploadImage } from 'apis/auction';
import { addImage, setAuctionFormState } from 'features/auctionFormSlice';
import useAppSelector from 'hooks/useAppSelector';

import styles from './ImageUploadButton.module.css';

const MAX_IMAGE_NUMBER = 10;
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB

interface ImageUploadButtonProps {
  setUploadingProgress: (percent: number) => void;
}

function ImageUploadButton({ setUploadingProgress }: ImageUploadButtonProps) {
  const dispatch = useDispatch();
  const { id, images } = useAppSelector((state) => state.auctionForm);

  const [isLoading, setIsLoading] = useState(false);

  const addSelectedImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('ho');
    if (e.target.files === null || e.target.files.length === 0) return;

    const selectedImages = Array.from(e.target.files);

    /* ERROR: Invalid File Type */
    if (selectedImages.some((file) => !file.type.startsWith('image/'))) {
      message.error(`이미지만 등록할 수 있습니다`);
      return;
    }

    /* ERROR: Exceed max image number */
    if (images.length + selectedImages.length > MAX_IMAGE_NUMBER) {
      message.error(`최대 ${MAX_IMAGE_NUMBER}개까지의 이미지만 등록할 수 있습니다`);
      return;
    }

    /* ERROR: Exceed max file size */
    if (selectedImages.some((file) => file.size > MAX_IMAGE_SIZE)) {
      message.error(`${MAX_IMAGE_SIZE / (1024 * 1024)}MB 이하의 이미지만 등록할 수 있습니다`);
      return;
    }

    if (!id) {
      startCreateAuction()
        .then((auction) => {
          dispatch(setAuctionFormState({ id: auction.id }));
          uploadImages(auction.id, selectedImages);
        })
        .catch((errMsg) => {
          if (errMsg !== '') message.error(errMsg);
        });
    } else uploadImages(id, selectedImages);
  };

  const uploadImages = async (auctionId: string, selectedImages: File[]) => {
    let uploadedImagesCount = 0;

    setIsLoading(true);
    setUploadingProgress(0);

    for (let idx = 0; idx < selectedImages.length; idx++) {
      const selectedImage = selectedImages[idx];

      try {
        const uploadedImage = await uploadImage(auctionId, selectedImage);
        dispatch(addImage(uploadedImage));
      } catch (errMsg: any) {
        if (errMsg) message.error(errMsg);
      } finally {
        setUploadingProgress((++uploadedImagesCount / selectedImages.length) * 100);
        if (uploadedImagesCount === selectedImages.length) {
          setTimeout(() => setIsLoading(false), 300);
        }
      }
    }
  };

  return (
    <label>
      <div className={styles.button}>
        {isLoading ? (
          <Icon loading name="spinner" color="grey" size="big" />
        ) : (
          <Icon name="camera" color="grey" size="big" />
        )}
        <p>
          <span className={styles.count}>{images.length}</span> / {MAX_IMAGE_NUMBER}
        </p>
      </div>
      <input
        type="file"
        name="files"
        value=""
        className={styles.input}
        multiple
        accept="image/*"
        onChange={addSelectedImages}
        disabled={isLoading}
      />
    </label>
  );
}

export default memo(ImageUploadButton);
